/**
 * Prefetch Floor Plans Data
 * 
 * This function makes the same API call as FloorPlansPage without storing the result.
 * It populates the HTTP cache so that when the user navigates to the floor plans page,
 * the data loads instantly from cache instead of making a fresh API call.
 * 
 * Uses the optimized custom API endpoint (/your_tour_api/floor_plans_page/{urlSlug})
 * which returns minimal data for the list view with coverImage resolved server-side.
 * 
 * Usage:
 * - Call from HomePage after initial render
 * - Call on nav link hover for immediate prefetch
 * - Non-blocking: Doesn't affect current page performance
 */

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { getDataVersion } from '../../utils/dataVersions';

const auth = getAuthClient();

let prefetchInProgress = false;
let prefetchComplete = false;
let hasUserVisitedFloorPlans = false; // Track if user has already visited floor plans page

// Call this from FloorPlansPage when it loads to mark that user has visited
export function markFloorPlansVisited() {
  hasUserVisitedFloorPlans = true;
}

export async function prefetchFloorPlans(communityNid, urlSlug, mode) {
  if (hasUserVisitedFloorPlans) {
    return;
  }

  if (prefetchInProgress || prefetchComplete) {
    return;
  }

  if (!urlSlug) {
    console.warn('[Prefetch] Missing urlSlug, skipping prefetch');
    return;
  }

  prefetchInProgress = true;

  try {
    const version = await getDataVersion( urlSlug, 'floorPlans', undefined, mode );
    const params = {
      ...( mode ? { mode } : {} ),
      ...( version ? { v: version } : {} ),
    };
    const url = buildYourTourApiUrl( `/your_tour_api/floor_plans_page/${urlSlug}`, params );
    await auth.fetchWithAuthentication(url, 'GET', null);

    prefetchComplete = true;
  } catch (error) {
    console.warn('[Prefetch] Floor plans prefetch failed (non-critical):', error);
    // Don't mark as complete on error so it can retry
  } finally {
    prefetchInProgress = false;
  }
}

// Reset prefetch state (useful for testing or when community changes)
export function resetPrefetch() {
  prefetchInProgress = false;
  prefetchComplete = false;
  hasUserVisitedFloorPlans = false;
}

